import { memo } from 'react'
import { ExternalLink, Calendar, User, FolderOpen } from 'lucide-react'
import type { JiraIssue } from '@/types/jira'

interface JiraTaskCardProps {
  task: JiraIssue
  compact?: boolean
}

const JIRA_BASE_URL = import.meta.env.VITE_JIRA_BASE_URL || 'https://feedbeq.atlassian.net'

const priorityColors: Record<string, string> = {
  Highest: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400',
  High: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400',
  Medium: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400',
  Low: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
  Lowest: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400',
}

const statusCategoryColors: Record<string, string> = {
  new: 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  indeterminate: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400',
  done: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
}

const JiraTaskCard = memo(function JiraTaskCard({ task, compact = false }: JiraTaskCardProps) {
  const { key, fields } = task
  const issueUrl = `${JIRA_BASE_URL}/browse/${key}`

  const statusColor = statusCategoryColors[fields.status.statusCategory.key] || statusCategoryColors.indeterminate
  const priorityColor = fields.priority ? priorityColors[fields.priority.name] || priorityColors.Medium : ''

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group">
        {fields.issuetype.iconUrl && (
          <img src={fields.issuetype.iconUrl} alt={fields.issuetype.name} className="w-4 h-4" />
        )}
        <a
          href={issueUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          {key}
        </a>
        <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">{fields.summary}</span>
        <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColor}`}>
          {fields.status.name}
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    )
  }

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {fields.issuetype.iconUrl && (
            <img src={fields.issuetype.iconUrl} alt={fields.issuetype.name} className="w-5 h-5" />
          )}
          <a
            href={issueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            {key}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="flex items-center gap-2">
          {fields.priority && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${priorityColor}`}>
              {fields.priority.name}
            </span>
          )}
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusColor}`}>
            {fields.status.name}
          </span>
        </div>
      </div>

      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 line-clamp-2">
        {fields.summary}
      </h4>

      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <FolderOpen className="h-3.5 w-3.5" />
          <span>{fields.project.key}</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          <span>{formatDate(fields.updated)}</span>
        </div>
        {fields.reporter && (
          <div className="flex items-center gap-1">
            <User className="h-3.5 w-3.5" />
            <span>{fields.reporter.displayName}</span>
          </div>
        )}
      </div>

      {fields.labels && fields.labels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {fields.labels.slice(0, 3).map(label => (
            <span
              key={label}
              className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
            >
              {label}
            </span>
          ))}
          {fields.labels.length > 3 && (
            <span className="text-xs text-gray-400">+{fields.labels.length - 3}</span>
          )}
        </div>
      )}
    </div>
  )
})

export default JiraTaskCard

